/**
 * usePushRegistration — auth-aware push setup hook.
 *
 * Waits for authStore.status to become "authenticated" before calling
 * registerPush(). This ensures the Cognito token is valid when the
 * POST /me/devices request fires.
 *
 * Lifecycle:
 *   - status: loading → (no-op, wait)
 *   - status: authenticated → registerPush() once per session
 *   - sign-out → teardownPush() (handled in authStore.signOutAll)
 *   - sign-in again → status flips back to authenticated → registerPush() again
 *     (registerPush has its own guard; teardownPush resets it on sign-out)
 *
 * No-ops entirely on web — registerPush() checks isNative() internally, but
 * the dynamic import is skipped here too so we never pull native modules
 * into the web bundle.
 */

import * as React from "react";
import { useAuthStore } from "@/store/authStore";
import { isMobileBuild } from "@/platform";

export function usePushRegistration(): void {
  const status = useAuthStore((s) => s.status);

  React.useEffect(() => {
    // Skip entirely on web / admin builds — no native plugins available.
    if (!isMobileBuild) return;
    if (status !== "authenticated") return;

    // Dynamic import keeps @capacitor-firebase/messaging out of the web chunk.
    console.log("[push] auth confirmed — starting push registration");
    import("@/native/notifications").then(({ registerPush }) => {
      void registerPush();
    });
  }, [status]);
}
